import { botLabel, colorOfSlot, currentGame, findBotModel, isGameOver, type PlayerSlot } from '@chess/core'
import { useEffect, useState, type JSX } from 'react'
import { GearIcon } from '../lib/AccountMenu.js'
import { roomUrl } from '../lib/router.js'
import { loadName, rememberRoom } from '../lib/storage.js'
import { BoardSkeleton } from '../room/BoardSkeleton.js'
import { Banner } from '../room/Overlays.js'
import { RoomView } from '../room/RoomView.js'
import { useRemoteRoom } from '../room/useRemoteRoom.js'

export interface RemoteGameProps {
  readonly roomId: string
  readonly playerKey: string | null
  readonly navigate: (to: string) => void
  readonly onSettings: () => void
}

export const RemoteGame = ({
  roomId,
  playerKey,
  navigate,
  onSettings,
}: RemoteGameProps): JSX.Element => {
  const connection = useRemoteRoom(roomId, playerKey, loadName())
  const { state: room, actor } = connection
  const mySlot: PlayerSlot | null = actor.kind === 'player' ? actor.slot : null
  const slot = currentGame(room)

  // Keep the home screen's room list current. This is a cache — the seat itself
  // lives in the link, so losing it costs the listing and nothing more.
  useEffect(() => {
    if (mySlot === null || playerKey === null) return
    const them: PlayerSlot = mySlot === 0 ? 1 : 0
    rememberRoom({
      roomId,
      key: playerKey,
      slot: mySlot,
      opponentName: room.players[them].name,
      timeControl: room.timeControl.kind === 'clock' ? `${room.timeControl.initialMs / 60_000} min` : 'no limit',
      wins: room.score.wins[mySlot],
      losses: room.score.wins[them],
      draws: room.score.draws,
      yourTurn: !isGameOver(slot.game.status) && slot.game.position.turn === colorOfSlot(mySlot, slot.index),
      finished: isGameOver(slot.game.status),
      updatedAt: Date.now(),
    })
  }, [mySlot, playerKey, room, roomId, slot])

  if (!connection.ready) return <BoardSkeleton />

  const opponentSlot: PlayerSlot | null = mySlot === null ? null : mySlot === 0 ? 1 : 0
  const opponentHere = opponentSlot === null ? true : room.players[opponentSlot].arrived
  const bot = room.opponent.kind === 'ai' ? findBotModel(room.opponent.model) : null
  const arenaModels = room.opponent.kind === 'ai-vs-ai' ? room.opponent.models : null

  return (
    <RoomView
      connection={connection}
      mySlot={mySlot}
      header={
        <header className="flex items-center justify-between px-2 py-2 safe-top sm:px-0">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm font-semibold text-ink-dim hover:text-ink"
          >
            ← Home
          </button>
          <span className="flex items-center gap-1">
            <span className="font-mono text-xs text-ink-dim">{roomId}</span>
            <button
              type="button"
              onClick={onSettings}
              aria-label="Settings"
              className="rounded-full p-1.5 text-ink-dim hover:text-ink"
            >
              <GearIcon />
            </button>
          </span>
        </header>
      }
      notice={
        arenaModels !== null ? (
          <Banner>
            {botLabel(arenaModels[0])} (White) vs {botLabel(arenaModels[1])} (Black) — an AI vs AI match. Nobody
            here can move; it plays itself out.
          </Banner>
        ) : mySlot === null ? (
          <Banner>You are watching. Only the two players can move.</Banner>
        ) : bot !== null ? (
          // There is no seat to fill, so no invite link — just say what you are
          // up against and how badly it is expected to go.
          <Banner>
            Playing {bot.label}. {bot.note}
          </Banner>
        ) : !opponentHere ? (
          <InviteCard roomId={roomId} />
        ) : null
      }
    />
  )
}

/**
 * Shown until the second seat is filled. The opponent's link is the one that
 * gets copied — deliberately not this player's own, which would hand over their
 * seat.
 */
const InviteCard = ({ roomId }: { readonly roomId: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void fetch(`/api/rooms/${encodeURIComponent(roomId)}/invite`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { inviteKey?: string } | null) => {
        if (cancelled || body?.inviteKey === undefined) return
        setInviteUrl(roomUrl(roomId, body.inviteKey))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [roomId])

  const copy = (): void => {
    if (inviteUrl === null) return
    void navigator.clipboard?.writeText(inviteUrl).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2_000)
    })
  }

  return (
    <div className="rounded-panel bg-panel p-3">
      <p className="text-sm font-semibold">Send this to your opponent</p>
      <div className="mt-2 flex gap-2">
        <input
          readOnly
          value={inviteUrl ?? 'Loading…'}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded bg-panel-raised px-3 py-2 font-mono text-xs text-ink outline-none"
        />
        <button
          type="button"
          onClick={copy}
          disabled={inviteUrl === null}
          className="shrink-0 rounded bg-accent px-4 py-2 text-sm font-bold text-black hover:bg-accent-strong disabled:opacity-50"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="mt-2 text-xs text-ink-dim">
        Keep the link in your address bar — it is how you get back to this seat.
      </p>
    </div>
  )
}
